import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Datepicker from "../../../../../components/common/Datepicker";
import planService from "../../../../../services/planService";
import Toaster from "../../../../../components/common/Toaster";
import moment from "moment";

const schema = yup.object({
  period_start: yup.string().required("Please select period start."),
  period_end: yup.string().required("Please select period end"),
  period_type: yup.object().shape({
    label: yup.string().required("Please select period."),
    value: yup.string().required("Please select period."),
  }),
});

const AccountPlanForm = ({ id, data, isLoading, setLoading, onSuccess }) => {
  const [periods, setPeriods] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await planService.saveAccountPlanAsync(id, data);
      Toaster.Success("Plan Assigned Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const data = planService.getPeriodTypes();
    setPeriods(data);
  }, []);

  useEffect(() => {
    const populateForm = () => {
      if (data.period_type) {
        const selected_period_type = {
          label: data.period_type_name,
          value: data.period_type,
        };

        setValue("period_type", selected_period_type);
      }

      if (data.period_start) {
        setValue("period_start", data.period_start);
        const period_start = new Date(data.period_start);
        setStartDate(period_start);
      }

      if (data.period_end) {
        setValue("period_end", data.period_end);
        const period_end = new Date(data.period_end);
        setEndDate(period_end);
      }
    };

    if (data) populateForm();
  }, [data, setValue]);

  const validDate = (current) => {
    return current.isAfter(startDate);
  };

  const handleStartDate = (value) => {
    const formatted_date = moment(value, moment.ISO_8601).format("YYYY-MM-DD");
    const period_start = new Date(moment(value));
    const period_end = period_start.setFullYear(period_start.getFullYear() + 1);

    setStartDate(formatted_date);
    setEndDate(period_end);
  };

  const handleEndDate = (value) => {
    const period_end = new Date(moment(value));
    setEndDate(period_end);
  };

  useEffect(() => {
    if (!data) setValue("period_type", periods[3]);
  }, [setValue, periods, data]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="period-type-dropdown"
            name="period_type"
            title="Period"
            options={periods}
            control={control}
            register={register("period_type")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <Datepicker
            id="period-start-datepicker"
            name="period_start"
            title="Period Start"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            initialValue={startDate}
            value={startDate}
            clearErrors={clearErrors}
            onDateChange={handleStartDate}
            register={register("period_start")}
            timeFormat={false}
            errors={errors}
            readOnly
          />
        </div>
        <div className="col-span-1">
          <Datepicker
            id="period-end-datepicker"
            name="period_end"
            title="Period End"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            onDateChange={handleEndDate}
            register={register("period_end")}
            value={endDate}
            initialValue={endDate}
            isValidDate={validDate}
            timeFormat={false}
            errors={errors}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default AccountPlanForm;
